/*
 * THE IN-SERVICE BOARD HEADER — #435 surface 2 (mockup handoff-02). Once a project
 * has been handed over (actualGoLive set), the board reads as a live service at a
 * glance: an "In service" pill, the day it went live, that it is now continuous,
 * and who owns it (P7 — a mode must answer "who"). Plus the one-time success ribbon
 * the moment the flip happens, which then clears.
 *
 * The board name itself lives in the sidebar Switcher, not a page title, so this
 * status band sits at the top of the board body — the first thing read on the
 * board — rather than beside a title the app does not render here.
 */
import { useState } from 'react';
import { useApp } from '../store';
import Icon from './Icon';
import { humanDay } from '../when';
import { isInService } from '../handoff';

/** How long after the handoff the green success ribbon still shows. A day: the
 *  person who marked it live sees it, and it has cleared by the time the team
 *  opens the board the next morning (the mockup's "then clears"). */
const RIBBON_WINDOW_MS = 24 * 60 * 60 * 1000;

function dayFromMs(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Session-scoped dismissal of the ribbon, keyed by board + the exact handoff
 *  moment — so a later revert-and-relive shows a fresh ribbon. sessionStorage, so
 *  it survives a reload within the session but never leaks to another viewer. */
function ribbonKey(boardId: string, at: number): string {
  return `fd.handoff.ribbon.${boardId}.${at}`;
}
function ribbonDismissed(boardId: string, at: number): boolean {
  try { return sessionStorage.getItem(ribbonKey(boardId, at)) === '1'; } catch { return false; }
}

export default function InServiceHeader() {
  const meta = useApp((s) => s.meta);
  const projectId = useApp((s) => s.projectId);
  const [, force] = useState(0);

  if (!isInService(meta)) return null;

  const liveMs = meta.actualGoLive as number;
  const owner = (meta.projectOwnerName ?? '').trim();
  const snapAt = meta.deliverySnapshot?.at;
  const showRibbon =
    snapAt != null &&
    Date.now() - snapAt < RIBBON_WINDOW_MS &&
    !ribbonDismissed(projectId, snapAt);

  const dismissRibbon = () => {
    if (snapAt == null) return;
    try { sessionStorage.setItem(ribbonKey(projectId, snapAt), '1'); } catch { /* fine without it */ }
    force((n) => n + 1);
  };

  return (
    <div className="isv-head">
      {showRibbon && (
        <div className="isv-ribbon" role="status">
          <Icon name="check" size={16} />
          <span>
            Marked live {humanDay(dayFromMs(liveMs))} — delivery recorded, this board flipped to
            continuous. The as-delivered record is kept, and logged in <b>Workspace History</b> and the steering pack.
          </span>
          <button className="isv-ribbon-x" aria-label="Dismiss" onClick={dismissRibbon}>
            <Icon name="close" size={14} />
          </button>
        </div>
      )}
      <div className="isv-status">
        <span className="isv-pill"><span className="isv-dot" aria-hidden="true" /> In service</span>
        <span className="isv-meta">
          Live since <b>{humanDay(dayFromMs(liveMs))}</b> · continuous{owner ? <> · owned by {owner}</> : null}
        </span>
      </div>
    </div>
  );
}
